import React, { useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  Container,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  useTheme,
  Link,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  InputAdornment,
  Fade,
  Zoom,
  styled,
  Box,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { gql, useMutation } from "@apollo/client";
import { motion, AnimatePresence } from "framer-motion";
import {
  EmailRounded,
  LockRounded,
  ArrowForwardRounded,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import OwwLogo from "../Assets/owwtransparentlogo.png";
import sidebardImage from "../Assets/newImage/header-image.png";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import LanguageSwitcher from "../components/LanguageSwitcher";

const GlassPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(145deg, #2a4b50 0%, #2a4b50 100%)`,
  backdropFilter: "blur(10px)",
  boxShadow: `${theme.shadows[10]}, inset 0 1px 1px rgba(255,255,255,0.1)`,
  border: "1px solid rgba(255,255,255,0.15)",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255,255,255,0.05)",
    zIndex: 0,
  },
}));
export default function LoginPage() {
  const theme = useTheme();
  const [login, { loading, error }] = useMutation(gql`
    mutation (
      $eventId: String!
      $role: String!
      $email: String!
      $password: String!
    ) {
      token: loginv2(
        eventId: $eventId
        role: $role
        email: $email
        password: $password
      )
    }
  `);
  const { t } = useTranslation();

  const navigate = useNavigate();
  let { eventId, role } = useParams();

  const onLogin = async (variables) => {
    return login({ variables }).then((resp) => {
      localStorage.setItem("auth.app.eventegration.com", resp.data.token);
      navigate("/demo");
    });
  };
  if (!eventId || !role) {
    // console.log("No eventId or role", eventId);
    eventId = "6220072402";
    role = "visitor";
    navigate(`/login/${eventId}/${role}`);
    return;
  }
  return (
    <>
      <LanguageSwitcher position="fixed" />

      <Grid
        container
        sx={{
          minHeight: "100vh",
          background: `linear-gradient(135deg, #2a4b50 30%, #2a4b50 90%)`,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: `url(${sidebardImage})`,
            backgroundSize: "cover",
            opacity: 0.05,
          },
        }}
      >
        <Grid
          item
          md={6}
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                // boxShadow: theme.shadows[10],
                position: "relative",
                "& img": {
                  width: "100%",
                  height: "auto",
                  objectFit: "cover",
                },
              }}
            >
              <img src={sidebardImage} alt="Conference" />
            </Box>
          </motion.div>
        </Grid>

        <Grid
          item
          md={6}
          xs={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Box
              sx={{
                textAlign: "center",
                mb: 2,
                "& img": {
                  width: 350,
                  filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.2))",
                },
              }}
            >
              <img
                src={OwwLogo}
                alt="Logo"
                style={{ width: "200px", objectFit: "contain" }}
              />
              <Typography
                variant="h3"
                sx={{
                  mt: 2,
                  fontWeight: 700,
                  letterSpacing: 1,
                  color: theme.palette.common.white,
                  textShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                {t("WelcomeBack")}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  mt: 1,
                  fontSize: "1.1rem",
                }}
              >
                {t("signToContinue")}
              </Typography>
            </Box>

            <GlassPaper sx={{ p: 2, borderRadius: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  color: theme.palette.common.white,
                  mb: 2,
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                {t("LoginPanel")}
              </Typography>

              <LoginForm
                onSubmit={onLogin}
                eventIdParam={eventId}
                roleParam={role}
              />

              <AnimatePresence>
                {loading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <LinearProgress
                      sx={{ mt: 3, height: 4, borderRadius: 2 }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {!!error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{ marginTop: 16 }}
                >
                  <Box
                    sx={{
                      p: 2,
                      background: theme.palette.error.dark,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <LockRounded fontSize="small" />
                    <Typography variant="body2" color="error.contrastText">
                      {error.message}
                    </Typography>
                  </Box>
                </motion.div>
              )}
            </GlassPaper>
          </motion.div>
        </Grid>
      </Grid>
    </>
  );
}

export function LoginForm({ onSubmit, eventIdParam, roleParam }) {
  const theme = useTheme();
  const { handleSubmit, control, formState } = useForm({
    defaultValues: {
      eventId: eventIdParam || "",
      role: roleParam || "visitor",
      email: "",
      password: "",
      rememberMe: false,
    },
  });
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");

  // Use the forgot password mutation
  const [forgotPassword] = useMutation(FORGOT_PASSWORD_MUTATION);

  const handleForgotPassword = async () => {
    setForgotLoading(true);
    setForgotError("");
    setForgotSuccess("");
    if (
      !forgotEmail ||
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(forgotEmail)
    ) {
      setForgotError(t("Please enter a valid email address."));
      setForgotLoading(false);
      return;
    }
    try {
      const { data } = await forgotPassword({
        variables: {
          role: roleParam || "visitor",
          eventId: eventIdParam || "",
          email: forgotEmail,
        },
      });

      if (data.forgotPassword.success) {
        setForgotSuccess(t("Password reset link sent to your email."));
        setTimeout(() => {
          setOpen(false);
        }, 2000);
      } else {
        setForgotError(
          data.forgotPassword.message ||
            t("Failed to send password reset link.")
        );
      }
    } catch (error) {
      setForgotError(
        t(error?.message || "An error occurred. Please try again.")
      );
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                label={t("Email")}
                variant="filled"
                error={!!error}
                helperText={error?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRounded sx={{ color: "rgba(255, 255, 255, 1)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiFilledInput-root": {
                    background: "rgba(255,255,255,0.1)",
                    borderRadius: 2,
                    color: "white",
                    "&:hover": {
                      background: "rgba(255,255,255,0.15)",
                      color: "white",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255,255,255,1)",
                  },
                }}
              />
            )}
          />

          <Controller
            control={control}
            name="password"
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                label={t("Password")}
                type="password"
                variant="filled"
                error={!!error}
                helperText={error?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRounded sx={{ color: "rgba(255,255,255,0.7)" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiFilledInput-root": {
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: 2,
                    color: "white",
                    "&:hover": {
                      background: "rgba(255,255,255,0.15)",
                      color: "white",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255,255,255,1)",
                  },
                }}
              />
            )}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <Controller
              control={control}
              name="rememberMe"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      sx={{
                        color: "white", // Ensure default color is white
                        "&.Mui-checked": {
                          color: "white", // Checked color
                        },
                      }}
                    />
                  }
                  label={t("RememberMe")}
                  sx={{
                    fontSize: "0.9rem",
                    color: "white", // Override label color
                    "& .MuiTypography-root": { color: "white" }, // Ensures text inside label is white
                  }}
                />
              )}
            />

            <Link
              component="button"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                setOpen(true);
              }}
              sx={{
                color: "rgba(255,255,255,0.7)",
                fontSize: "0.9rem",
                "&:hover": {
                  color: theme.palette.secondary.main,
                },
              }}
            >
              {t("ForgotPassword?")}
            </Link>
          </Box>

          <LoadingButton
            fullWidth
            size="large"
            variant="gradient"
            loading={formState.isSubmitting}
            endIcon={<ArrowForwardRounded />}
            type="submit"
            sx={{
              mt: 2,
              background: `linear-gradient(45deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
              color: theme.palette.common.white,
              boxShadow: theme.shadows[3],
              "&:hover": {
                boxShadow: theme.shadows[6],
                transform: "translateY(-1px)",
              },
              "&.MuiLoadingButton-loading": {
                background: theme.palette.secondary.dark,
              },
            }}
          >
            {t("SignIn")}
          </LoadingButton>
        </Stack>
      </form>

      {/* Enhanced Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            background: `linear-gradient(145deg, #2a4b50 0%, #2a4b50 100%)`,
            borderRadius: 4,
            boxShadow: theme.shadows[10],
          },
        }}
      >
        <DialogTitle
          sx={{
            color: theme.palette.common.white,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          {t("ResetPassword")}
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          <TextField
            fullWidth
            variant="filled"
            type="email"
            label={t("Email")}
            required
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailRounded sx={{ color: "rgba(255,255,255,0.7)" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiFilledInput-root": {
                background: "rgba(255,255,255,0.1)",
                borderRadius: 2,
                mt: 2,
                color: "white",
              },
              "& .MuiInputLabel-root": {
                color: "rgba(255,255,255,1)",
              },
            }}
          />

          <AnimatePresence>
            {forgotError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                style={{ overflow: "hidden" }}
              >
                <Typography
                  color="error"
                  sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <LockRounded fontSize="small" />
                  {forgotError}
                </Typography>
              </motion.div>
            )}

            {forgotSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <Typography color="success.main" sx={{ mt: 2 }}>
                  {forgotSuccess}
                </Typography>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            borderTop: "1px solid rgba(255,255,255,0.1)",
            gap: 2,
          }}
        >
          <Button
            variant="text"
            onClick={() => setOpen(false)}
            sx={{ color: "rgba(255,255,255,0.7)", backgroundColor: "red" }}
          >
            {t("Cancel")}
          </Button>
          <LoadingButton
            variant="contained"
            loading={forgotLoading}
            onClick={handleForgotPassword}
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
              color: theme.palette.common.white,
              boxShadow: theme.shadows[2],
              "&:hover": {
                boxShadow: theme.shadows[4],
              },
            }}
          >
            {t("SendLink")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

LoginForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  eventIdParam: PropTypes.string,
  roleParam: PropTypes.string,
};

const FORGOT_PASSWORD_MUTATION = gql`
  mutation ForgotPassword($role: String!, $eventId: String!, $email: String!) {
    forgotPassword(role: $role, eventId: $eventId, email: $email) {
      success
      message
    }
  }
`;
